import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { NoAvatar } from 'assets/svgs';
import { OnlineIndicator } from 'components';

function Avatar({
  avatarUrl,
  alt,
  showConnector,
  commentRef,
  lastReplyRef,
  replyRefs,
  level,
  isOnline
}) {
  const [connectorHeight, setConnectorHeight] = useState(120);
  const [horizontalConnectors, setHorizontalConnectors] = useState([]);

  useEffect(() => {
    if (showConnector && commentRef?.current) {
      const calculateHeight = () => {
        let calculatedHeight = 120; // default height
        const connectors = [];

        if (lastReplyRef?.current) {
          // Calculate distance from current avatar to last reply's avatar
          const parentRect = commentRef.current.getBoundingClientRect();
          const replyRect = lastReplyRef.current.getBoundingClientRect();

          // Get the avatar position within the comment
          const avatarHeight = window.innerWidth >= 768 ? 46 : 20;
          const avatarCenterY = avatarHeight / 2;

          // Find the actual avatar within the last reply
          const lastReplyTopLine =
            lastReplyRef.current.querySelector('[class*="TopLine"]');
          let distanceToReply;

          if (lastReplyTopLine) {
            const lastReplyTopLineRect =
              lastReplyTopLine.getBoundingClientRect();
            // The avatar is positioned at the top of the TopLine container
            const lastReplyAvatarY = lastReplyTopLineRect.top + avatarCenterY;
            const parentAvatarY = parentRect.top + avatarCenterY;
            distanceToReply = lastReplyAvatarY - parentAvatarY;
          } else {
            // Fallback to comment container position if TopLine not found
            distanceToReply = replyRect.top - parentRect.top + avatarCenterY;
          }

          calculatedHeight = Math.max(
            distanceToReply -
              (level === 0 && window.innerWidth >= 768
                ? 52
                : level === 0 && window.innerWidth < 768
                  ? 40
                  : level > 0 && window.innerWidth < 768
                    ? 30
                    : 38),
            60
          );

          // Calculate horizontal connector positions for each reply
          if (replyRefs?.current) {
            replyRefs.current.forEach((replyRef, index) => {
              if (replyRef) {
                // Find the TopLine container within the reply
                const replyTopLine =
                  replyRef.querySelector('[class*="TopLine"]');
                if (replyTopLine) {
                  const replyTopLineRect = replyTopLine.getBoundingClientRect();
                  const parentAvatarY = parentRect.top + avatarCenterY;

                  // Calculate Y position relative to parent avatar center
                  const replyAvatarY = replyTopLineRect.top + avatarCenterY;
                  const replyDistance = replyAvatarY - parentAvatarY;
                  const svgYPosition =
                    replyDistance -
                    (level === 0 && window.innerWidth >= 768
                      ? 72
                      : level === 0 && window.innerWidth < 768
                        ? 60
                        : level > 0 && window.innerWidth < 768
                          ? 50
                          : 58); // Adjust for overshoot

                  connectors.push({
                    id: index,
                    y: Math.max(svgYPosition, 10)
                  });
                } else {
                  // Fallback if TopLine not found
                  const currentReplyRect = replyRef.getBoundingClientRect();
                  const replyDistance =
                    currentReplyRect.top - parentRect.top + avatarCenterY;
                  const svgYPosition = replyDistance - avatarCenterY + 10;
                  connectors.push({
                    id: index,
                    y: Math.max(svgYPosition, 10)
                  });
                }
              }
            });
          }
        } else if (commentRef?.current) {
          // Fallback to comment height if no reply ref
          const commentHeight = commentRef.current.offsetHeight;
          const avatarHeight = window.innerWidth >= 768 ? 46 : 20;
          const paddingOffset = window.innerWidth >= 768 ? 30 : 20;
          calculatedHeight = Math.max(
            commentHeight - avatarHeight - paddingOffset,
            60
          );
        }

        setConnectorHeight(calculatedHeight);
        setHorizontalConnectors(connectors);
      };

      // Initial calculation
      setTimeout(calculateHeight, 100);

      const resizeObserver = new ResizeObserver(() => {
        setTimeout(calculateHeight, 100);
      });

      if (commentRef?.current) {
        resizeObserver.observe(commentRef.current);
      }

      if (lastReplyRef?.current) {
        resizeObserver.observe(lastReplyRef.current);
      }

      // Observe all reply refs
      if (replyRefs?.current) {
        replyRefs.current.forEach((replyRef) => {
          if (replyRef) {
            resizeObserver.observe(replyRef);
          }
        });
      }

      const handleResize = () => {
        setTimeout(calculateHeight, 150);
      };

      window.addEventListener('resize', handleResize);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [showConnector, commentRef, lastReplyRef, replyRefs]);

  return (
    <Container>
      {showConnector && (
        <ConnectorSvg
          className="connector-svg"
          width={window.innerWidth < 768 ? '15' : '47'}
          height={connectorHeight}
          viewBox={`0 0 ${window.innerWidth < 768 ? '15' : '47'} ${connectorHeight}`}
          xmlns="http://www.w3.org/2000/svg"
          $level={level}>
          {/* Main vertical line */}
          <path
            stroke="#C8CCD1"
            strokeWidth="2"
            fill="none"
            d={`M${window.innerWidth < 768 ? '7.5' : '23'} 10v${connectorHeight - 30}`}
          />
          {/* Horizontal connectors for each reply */}
          {horizontalConnectors.map((connector) => (
            <path
              key={connector.id}
              stroke="#C8CCD1"
              strokeWidth="2"
              fill="none"
              d={`M${window.innerWidth < 768 ? '7.5' : '23'} ${connector.y}c0 9.941 8.059 18 ${window.innerWidth < 768 ? '7.5' : '24'} 18`}
            />
          ))}
        </ConnectorSvg>
      )}
      {avatarUrl ? (
        <motion.img
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          src={avatarUrl}
          alt={alt}
        />
      ) : (
        <NoAvatar width="100%" height="100%" />
      )}
      <OnlineIndicatorContainer>
        <OnlineIndicator
          isOnline={isOnline}
          size={window.innerWidth < 768 ? '8px' : '12px'}
        />
      </OnlineIndicatorContainer>
    </Container>
  );
}

const Container = styled.div`
  flex-shrink: 0;
  height: 20px;
  overflow: visible;
  position: relative;
  width: 20px;

  @media (min-width: 768px) {
    width: 46px;
    height: 46px;
    bottom: 1px;
  }

  img {
    background: transparent;
    border-radius: 50%;
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`;

const ConnectorSvg = styled.svg`
  left: 50%;
  position: absolute;
  top: 100%;
  transform: translateX(-50%);
  z-index: 0;

  @media (max-width: 767px) {
    width: 15px;
    transform: translateX(-50%);
  }

  @media (min-width: 768px) {
    width: 47px;
  }
`;

const OnlineIndicatorContainer = styled.div`
  position: absolute;
  right: -2px;
  top: -2px;
  z-index: 1;
`;

export default Avatar;
